import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Grid } from "@mui/material";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useCreateBranchTallyMutation, useLazyCheckInProgressBranchTallyQuery } from "store/services/branchTallyApi";
import { useLazySearchBranchQuery } from "store/services/branchApi";
import { useLazySearchUsersQuery } from "store/services/userApi";
import { useAuth } from "hooks";

// Custom debounce hook
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = React.useState(value);

    React.useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

const BranchTallyCreateScreen = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const currentUserId = useSelector((state) => state.auth.user?._id);

    const { viewEntity } = useAuth();
    const account = viewEntity?.entity?.account?._id;
    const entity = viewEntity?.entity?._id;

    const [branch, setBranch] = React.useState(null);
    const [branchSearch, setBranchSearch] = React.useState("");
    const debouncedBranchSearch = useDebounce(branchSearch, 400);

    const [participants, setParticipants] = React.useState([]);
    const [userSearch, setUserSearch] = React.useState("");
    const debouncedUserSearch = useDebounce(userSearch, 400);

    const [triggerBranchDDL, { data: branchOptions = [] }] = useLazySearchBranchQuery();
    const [triggerUsersDDL, { data: userOptions = [] }] = useLazySearchUsersQuery();
    const [triggerCheck, { data: inProgressCheck }] = useLazyCheckInProgressBranchTallyQuery();
    const [createBranchTally, { isLoading: creating }] = useCreateBranchTallyMutation();

    const breadcrumbs = [
        { title: "Branch Tally", path: "/app/crm/branch-tally/list" },
        { title: "New Tally" },
    ];

    React.useEffect(() => {
        if (!account || !entity) return;
        if (debouncedBranchSearch.trim().length < 3) return;
        triggerBranchDDL({ account, entity, q: debouncedBranchSearch });
    }, [debouncedBranchSearch, triggerBranchDDL, account, entity]);

    React.useEffect(() => {
        if (!entity) return;
        if (debouncedUserSearch.trim().length < 3) return;
        triggerUsersDDL({ q: debouncedUserSearch, entity });
    }, [debouncedUserSearch, triggerUsersDDL, entity]);

    React.useEffect(() => {
        if (location.state?.branch) {
            setBranch(location.state.branch);
        }
    }, [location.state]);

    React.useEffect(() => {
        setParticipants([]);
        setUserSearch("");
        if (branch?._id) {
            triggerCheck({ branch: branch._id });
        }
    }, [branch?._id, triggerCheck]);

    const isJoinMode = inProgressCheck?.exists && !inProgressCheck?.alreadyJoined;
    const isAlreadyJoined = inProgressCheck?.exists && inProgressCheck?.alreadyJoined;

    const handleSubmit = async () => {
        try {
            const payload = {
                branch: branch?._id,
                participants: participants.map((u) => u._id).filter(Boolean),
            };
            const res = await createBranchTally(payload).unwrap();
            toast.success(res.joined ? "Joined existing tally!" : "Branch tally started!");
            navigate(`/app/crm/branch-tally/scan/${res.tally._id}`);
        } catch (err) {
            toast.error(err?.data?.message ?? "Couldn't start tally");
        }
    };

    const buttonLabel = creating
        ? isJoinMode
            ? "Joining..."
            : "Starting..."
        : isJoinMode
            ? "Join Tally"
            : isAlreadyJoined
                ? "Resume Scanning"
                : "Start Tally";

    return (
        <BreadcrumbLayout breadcrumbs={breadcrumbs} isBusy={creating}>
            <BreadcrumbLayout.Paper>
                <AppForm>
                    <AppForm.Content>
                        <Grid container spacing={4}>
                            {/* Branch Field - taking half width on medium screens and up */}
                            <Grid item xs={12} md={6}>
                                <BaseAutocomplete
                                    fullWidth
                                    label="Search Branch"
                                    placeholder="Type at least 3 characters..."
                                    options={branchOptions}
                                    getOptionLabel={(opt) => opt ? `${opt?.name} - ${opt?.code || ""}` : ""}
                                    isOptionEqualToValue={(o, v) => o?._id === v?._id}
                                    value={branch}
                                    onChange={(_, v) => setBranch(v)}
                                    onInputChange={(_, v) => setBranchSearch(v || "")}
                                    noOptionsText={branchSearch.length < 3 ? "Type at least 3 characters..." : "No branches found"}
                                />
                            </Grid>

                            {/* Participant Field - taking half width with left margin */}
                            {branch?._id && !inProgressCheck?.exists && (
                                <Grid item xs={12} md={6} style={{ marginLeft: { md: "8px" } }}>
                                    <BaseAutocomplete
                                        multiple
                                        fullWidth
                                        label="Search Participants"
                                        placeholder="Type at least 3 characters..."
                                        options={userOptions}
                                        getOptionLabel={(opt) => opt?.name || ""}
                                        isOptionEqualToValue={(o, v) => o?._id === v?._id}
                                        value={participants}
                                        onChange={(_, v) => setParticipants(v)}
                                        onInputChange={(_, v) => setUserSearch(v || "")}
                                        noOptionsText={userSearch.length < 3 ? "Type at least 3 characters..." : "No users found"}
                                    />
                                </Grid>
                            )}

                            {isJoinMode && (
                                <Grid item xs={12}>
                                    <div style={{ color: "#b45309", fontSize: 14, fontWeight: 600, padding: "8px 0" }}>
                                        An active tally is already in progress for this branch. Click Join Tally to participate.
                                    </div>
                                </Grid>
                            )}
                        </Grid>
                    </AppForm.Content>
                    <AppForm.Actions paddingX={3} paddingY={2} gap={2} style={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button onClick={() => navigate("/app/crm/branch-tally/list")}>Cancel</Button>
                        <Button
                            variant="contained"
                            color={isJoinMode ? "warning" : "primary"}
                            onClick={handleSubmit}
                            disabled={!branch || creating}
                        >
                            {buttonLabel}
                        </Button>
                    </AppForm.Actions>
                </AppForm>
            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};

export default BranchTallyCreateScreen;